var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { loadModule } from '../../module-loader';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { jsx } from '@emotion/react';
import classNames from 'classnames';
import * as React from 'react';
import { FixedPosition } from '../../types/app-config';
import { currentDialogChanged, dialogInfosChanged, activePagePartChanged } from '../../app-actions';
import { changeDialog } from '../../browser-history';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash-es';
import defaultMessages from '../../translations/default';
import { AppMode, BrowserSizeMode, PagePart } from '../../types/common';
import { SCREEN_RESOLUTIONS } from '../../constants';
import * as SeamlessImmutable from 'seamless-immutable';
import { getAppStore } from '../../store';
import { getGlobalStylesForFixed, getStylesForFixed, getResizeStyle } from '../styles/styles';
import DialogAnimation from './animation';
import { clearSelection, loadLayoutEntry } from '../utils/utils';
import { bindResizeHandler } from '../utils/resize-utils';
import { getJimuThemeModule, getLocalStorageAppKey, readLocalStorage, setLocalStorage } from '../../utils/utils';
import { getDialogIdFromIdOrLabel } from '../../utils/url-utils';
import { focusElementInKeyboardMode } from '../../accessibility';
const { useEffect, useState, useRef } = React;
let disableWindowFocus = null;
function FixedDialog(_a) {
    var _b, _c, _d, _e, _f;
    var { dialogJson } = _a, props = __rest(_a, ["dialogJson"]);
    const [jimuUI, setJimuUI] = useState(null);
    const [jimuLayout, setJimuLayout] = useState(null);
    const [getAppConfigAction, setGetAppConfigAction] = useState(null);
    const [interact, setInteract] = useState(null);
    const disableWindowFocusFromURL = useSelector((state) => state.queryObject.disable_window_focus);
    if (disableWindowFocus === null) {
        disableWindowFocus = disableWindowFocusFromURL || 'false';
    }
    const runtimeMode = useSelector((state) => state.queryObject.draft) ? 'draft' : 'published';
    const timestamp = useSelector((state) => state.appConfig.timestamp);
    const currentPageId = useSelector((state) => state.appRuntimeInfo.currentPageId);
    const currentPageDlgId = useSelector((state) => state.appConfig.pages[currentPageId].autoOpenDialogId);
    const currentDlgId = useSelector((state) => state.appRuntimeInfo.currentDialogId);
    const isDesignMode = useSelector((state) => state.appRuntimeInfo.appMode !== AppMode.Run);
    const isExpressionMode = useSelector((state) => state.appRuntimeInfo.appMode === AppMode.Express);
    const isLayoutLock = useSelector((state) => { var _a, _b; return (_b = (_a = state.appConfig) === null || _a === void 0 ? void 0 : _a.forBuilderAttributes) === null || _b === void 0 ? void 0 : _b.lockLayout; });
    const browserSizeMode = useSelector((state) => state.browserSizeMode);
    const viewPort = useSelector((state) => { var _a, _b, _c; return (_c = (_b = (_a = state.appConfig) === null || _a === void 0 ? void 0 : _a.forBuilderAttributes) === null || _b === void 0 ? void 0 : _b.viewPortSize) === null || _c === void 0 ? void 0 : _c[browserSizeMode]; }) || SCREEN_RESOLUTIONS[browserSizeMode][1];
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const browserHeight = window.jimuConfig.isInBuilder ? viewPort.height : windowHeight;
    const dispatch = useDispatch();
    const messages = useFormatMessage();
    const urlDlgId = useSelector((state) => getDialogIdFromIdOrLabel(state.queryObject.dlg, state.appConfig));
    const dialogInfos = useSelector((state) => state.appRuntimeInfo.dialogInfos); // ?? Immutable({});
    const splashId = useSelector((state) => {
        var _a;
        return dialogJson.isSplash
            ? dialogJson.id
            : state.appConfig.dialogs && ((_a = state.appConfig.dialogs[Object.keys(state.appConfig.dialogs).find(dlgId => state.appConfig.dialogs[dlgId].isSplash)]) === null || _a === void 0 ? void 0 : _a.id);
    }, shallowEqual);
    // false for toc/page-click
    const [isOpen, setIsOpen] = useState((props.isOpenByPage && currentDlgId === splashId) || (dialogJson.id === urlDlgId && (currentDlgId !== urlDlgId)) || currentDlgId === dialogJson.id);
    const dlgId = props.isOpenByPage ? currentPageId + '-' + dialogJson.id : dialogJson.id;
    const [isChecked, setCheckState] = useState((dialogInfos && dialogInfos[dlgId]) ? dialogInfos[dlgId].checked : false);
    const dialogSizeMode = dialogJson.sizeMode[browserSizeMode] || dialogJson.sizeMode[BrowserSizeMode.Large];
    const centered = dialogSizeMode.position === FixedPosition.MiddleCenter;
    const resizeRef = useRef(null);
    const [interactable, setInteractable] = useState(null);
    const [fade, setFadeState] = useState(true);
    const [cbxAnimation, setCbxAnimation] = useState(false);
    const isDialogClosed = (dialogId) => {
        var _a;
        return (_a = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[dialogId]) === null || _a === void 0 ? void 0 : _a.isClosed;
    };
    // check if support resizing.
    const supportResizing = () => {
        return isDesignMode && (isExpressionMode || !isLayoutLock) && window.jimuConfig.isInBuilder;
    };
    const resizeHeight = () => {
        setWindowHeight(window.innerHeight);
    };
    useEffect(() => {
        if (supportResizing() && getAppConfigAction && interact) {
            const debounceFunc = debounce(() => {
                if (interactable) {
                    interactable.unset();
                }
                if (resizeRef.current) {
                    const currentBrowserSizeMode = dialogJson.sizeMode[browserSizeMode] ? browserSizeMode : BrowserSizeMode.Large;
                    setInteractable(bindResizeHandler(interact, resizeRef.current, dialogJson, currentBrowserSizeMode, getAppConfigAction));
                }
            }, resizeRef.current ? 0 : 1000);
            debounceFunc();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesignMode, isLayoutLock, currentDlgId, dialogJson.sizeMode[browserSizeMode], dialogSizeMode.position, dialogSizeMode.width, dialogSizeMode.height, getAppConfigAction, interact]);
    useEffect(() => {
        setCheckState(false);
    }, [currentDlgId]);
    useEffect(() => {
        var _a;
        Promise.all([loadJimuUIForFixed(), loadLayoutEntry()]).then(results => {
            setJimuUI(results[0]);
            setJimuLayout(results[1]);
        });
        if (window.jimuConfig.isInBuilder) {
            if (!getAppConfigAction) {
                import('jimu-for-builder').then(module => {
                    setGetAppConfigAction(() => module.getAppConfigAction);
                });
            }
            if (!interact) {
                import('jimu-core/dnd').then(module => {
                    setInteract(() => module.interact);
                });
            }
        }
        else if (!window.jimuConfig.isBuilder) { //for runtime
            if ((_a = dialogSizeMode.height) === null || _a === void 0 ? void 0 : _a.includes('%')) { //no height for side dialog
                window.addEventListener('resize', resizeHeight);
            }
        }
        // dispatch dialog when:
        // 1. splash when it's not closed by closedIds or localStorageIds.
        // 2. page dialog when splash is closed already (in closedIds or localStorageIds).
        const keys = getDialogUniqueKey(splashId, runtimeMode, timestamp);
        const isInStore = readLocalStorage(keys.newKey) === 'true' || readLocalStorage(keys.oldKey) === 'true';
        if (((dialogJson.isSplash && (!isDialogClosed(splashId) && (window.jimuConfig.isInBuilder || !isInStore))) ||
            (props.isOpenByPage &&
                ((splashId && (isDialogClosed(splashId) || (window.jimuConfig.isInBuilder || isInStore))) || !splashId))) &&
            !checkIsInLocalStore({
                currentPageId,
                dialogProps: { dialogJson, runtime: props.runtime, isOpenByPage: props.isOpenByPage },
                runtimeMode,
                timestamp,
                dialogInfos,
                dispatch
            }) &&
            dialogJson.id !== urlDlgId &&
            // (dialogJson.id !== urlDlgId || getAppStore().getState().appConfig.dialogs[currentDlgId].isSplash) && //except fixedBySplashOrPage.
            dialogJson.id !== currentDlgId) {
            dispatch(currentDialogChanged(dialogJson.id));
            dispatch(activePagePartChanged(null));
        }
        // fixed -> fixed, only rerender
        // splash->fixed, page->fixed, will call willUnmount.
        return () => {
            var _a;
            if (isDesignMode && interactable) {
                interactable.unset();
            }
            //for runtime & %
            if (!window.jimuConfig.isBuilder && ((_a = dialogSizeMode.height) === null || _a === void 0 ? void 0 : _a.includes('%'))) {
                window.removeEventListener('resize', resizeHeight);
            }
            const state = getAppStore().getState();
            const dlgInfos = state.appRuntimeInfo.dialogInfos;
            if (dlgInfos) {
                const dlgInfo = dlgInfos[getDlgId()]; // get latest infos
                if (dlgInfo === null || dlgInfo === void 0 ? void 0 : dlgInfo.canClose) { // && !dlgInfo?.isClosed){
                    // closeDialog(dlgInfos);
                    closeDialog(state);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const alertVersion = (_b = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[currentDlgId]) === null || _b === void 0 ? void 0 : _b.alertVersion;
    useEffect(() => {
        if (alertVersion >= 1) {
            setCbxAnimation(true);
            const debounceFunc = debounce(() => {
                setCbxAnimation(false);
            }, 800);
            debounceFunc();
        }
    }, [alertVersion]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const linkDialogId = readLocalStorage('exb-link-dialog');
        if (linkDialogId) {
            return;
        }
        let _isOpen = false;
        if (dialogJson.isSplash || props.isOpenByPage || currentDlgId) {
            const storeProps = {
                currentPageId,
                dialogProps: { dialogJson, runtime: props.runtime, isOpenByPage: props.isOpenByPage },
                runtimeMode,
                timestamp,
                dialogInfos,
                dispatch,
                urlDlgId
            };
            _isOpen = !checkIsInLocalStore(storeProps);
        }
        if (_isOpen !== isOpen) {
            setIsOpen(_isOpen);
        }
    });
    // Click overlay
    const onToggle = () => {
        var _a, _b;
        if (isDesignMode && !isExpressionMode) {
            // clear current widget selection to select dialog when clicking mask.
            clearSelection(dispatch);
            return;
        }
        else if (dialogJson.interactionType && dialogJson.confirmBeforeClose && !isChecked) { // TODO: use info
            const newDlgInfos = (dialogInfos || SeamlessImmutable({}))
                .set(getDlgId(), {
                canClose: false,
                checked: false,
                alertVersion: ((_b = (_a = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[getDlgId()]) === null || _a === void 0 ? void 0 : _a.alertVersion) !== null && _b !== void 0 ? _b : 0) + 1,
                isClosed: false
            });
            dispatch(dialogInfosChanged(newDlgInfos));
            return;
        }
        if (isOpen && dialogJson.closeWhenClickOverlay) {
            closeDialog();
        }
        else {
            clearSelection(dispatch);
        }
    };
    const closeDialog = (state) => {
        var _a, _b, _c;
        disableWindowFocus = 'false';
        if (dialogJson.isSplash || props.isOpenByPage) {
            const storeProps = {
                currentPageId,
                dialogProps: { dialogJson, runtime: props.runtime },
                runtimeMode,
                timestamp,
                dialogInfos: ((_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.dialogInfos) || dialogInfos,
                dispatch
            };
            setDialogInLocalStore(storeProps);
            const nextDlgId = getNextDlgId(state);
            dispatch(currentDialogChanged(nextDlgId));
            setIsOpen(false);
            if (nextDlgId) {
                // focus on first node of next dialog
                const firstNode = (_b = document.getElementById(nextDlgId)) === null || _b === void 0 ? void 0 : _b.parentNode;
                focusElementInKeyboardMode(firstNode);
            }
            else {
                changeDialog(null); // update state to back to page tab on TOC.
                dispatch(activePagePartChanged(PagePart.Body));
            }
        }
        else {
            setDialogInDialogInfos(((_c = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _c === void 0 ? void 0 : _c.dialogInfos) || dialogInfos, dialogJson.id, dispatch);
            changeDialog(null);
            dispatch(currentDialogChanged(null));
            dispatch(activePagePartChanged(PagePart.Body));
        }
    };
    /**
     *
     */
    const getDlgId = () => {
        return props.isOpenByPage ? currentPageId + '-' + dialogJson.id : dialogJson.id;
    };
    const getNextDlgId = (state) => {
        var _a, _b, _c, _d, _e, _f, _g;
        // when trying to open a fixed dlg from splash/page (no other behind dialogs), url is already changed before urlDlgId updated (use latest urlDlgId).
        let newUrlDlg = urlDlgId;
        let newCurrentDlgId = currentDlgId;
        if (dialogJson.isSplash || props.isOpenByPage) {
            newUrlDlg = state === null || state === void 0 ? void 0 : state.queryObject.dlg;
            newCurrentDlgId = (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.currentDialogId;
            // For TOC/dialog list, open next dlg when current is splash
            if (window.jimuConfig.isInBuilder) { // open current currentDlgId instead of page dlgId
                if (dialogJson.isSplash) {
                    // open pageDlg by link or confirmation/close when splash is closed and pageDlg is set&open (when app starts).
                    if (currentPageDlgId && !((_d = (_c = (state ? (_b = state.appRuntimeInfo) === null || _b === void 0 ? void 0 : _b.dialogInfos : dialogInfos)) === null || _c === void 0 ? void 0 : _c[currentPageId + '-' + currentPageDlgId]) === null || _d === void 0 ? void 0 : _d.isClosed)) {
                        return currentPageDlgId;
                    }
                    // close splash when it `isClose` is true and not trying to open another fixed. (steps: confirmation/close triggers splash's unmount/close again.)
                    if (dialogJson.isSplash && newUrlDlg === dialogJson.id && ((_g = (_f = (_e = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _e === void 0 ? void 0 : _e.dialogInfos) === null || _f === void 0 ? void 0 : _f[dialogJson.id]) === null || _g === void 0 ? void 0 : _g.isClosed)) {
                        return null;
                    }
                }
                return newUrlDlg;
            }
        }
        let nextDlgId = (dialogJson.isSplash && (currentPageDlgId || newUrlDlg)) || (props.isOpenByPage && newCurrentDlgId !== newUrlDlg && newUrlDlg) || null;
        // if((dialogJson.isSplash || props.isOpenByPage) && dialogJson.id !== nextDlgId){
        // } else
        if (newCurrentDlgId === nextDlgId) {
            nextDlgId = null;
        }
        return nextDlgId;
    };
    const setCbxState = (checked) => {
        setCheckState(checked);
        const canClose = dialogJson.interactionType && dialogJson.confirmBeforeClose ? checked : true;
        const newDlgInfos = (dialogInfos || SeamlessImmutable({})).set(getDlgId(), { canClose: canClose, checked: checked, isClosed: false });
        dispatch(dialogInfosChanged(newDlgInfos));
    };
    const onModalFadeEnd = () => {
        setFadeState(false);
    };
    if (!jimuUI || !jimuLayout) {
        return null;
    }
    const { Modal } = jimuUI;
    const Global = getJimuThemeModule().Global;
    const hasBackDrop = dialogJson.blockPage || dialogJson.isOpenerDlg;
    return (jsx(Modal, { "aria-label": dialogJson.label, autoFocus: disableWindowFocus !== 'true', isOpen: isOpen, fade: false, disableActivateOverlay: true, toggle: onToggle, centered: centered, css: getStylesForFixed(dialogSizeMode, browserHeight, dialogJson.interactionType), id: dialogJson.id, className: 'app-dialog w-100 h-100', wrapClassName: `app-dialog-wrap app-dialog-wrap-${dialogJson.id} ${hasBackDrop === true ? '' : 'no-backdrop'}`, modalClassName: 'app-dialog-modal', backdropClassName: 'app-dialog-backdrop', backdrop: hasBackDrop === true, "aria-modal": hasBackDrop || false, contentClassName: 'app-dialog-content', onOpened: onModalFadeEnd },
        jsx(Global, { styles: getGlobalStylesForFixed(dialogJson, isOpen, browserHeight, dialogSizeMode) }),
        jsx("div", { className: 'app-dialog-resize-container', css: getResizeStyle(), ref: el => { resizeRef.current = el; } },
            jsx("div", { className: 'w-100 h-100' }, isOpen && !fade &&
                jsx(DialogAnimation, { dialogJson: dialogJson, isOpen: isOpen, jimuUI: jimuUI, jimuLayout: jimuLayout, cbxAnimation: cbxAnimation, messages: messages, isChecked: isChecked, setCbxState: setCbxState, closeDialog: closeDialog, loadingCallBack: (loading) => null })),
            supportResizing() &&
                jsx("div", { className: classNames('app-dialog-resize-handlers') }, isDesignMode && jsx(React.Fragment, null,
                    (!((_c = dialogSizeMode === null || dialogSizeMode === void 0 ? void 0 : dialogSizeMode.position) === null || _c === void 0 ? void 0 : _c.includes('TOP')) || dialogSizeMode.heightMode === 'STRETCH') &&
                        jsx("span", { key: '1', className: 'resize-handler dialog-resize-top' },
                            jsx("div", { className: 'resize-handler-icon h-100' })),
                    (!((_d = dialogSizeMode === null || dialogSizeMode === void 0 ? void 0 : dialogSizeMode.position) === null || _d === void 0 ? void 0 : _d.includes('BOTTOM')) || dialogSizeMode.heightMode === 'STRETCH') &&
                        jsx("span", { key: '2', className: 'resize-handler dialog-resize-bottom' },
                            jsx("div", { className: 'resize-handler-icon h-100' })),
                    (!((_e = dialogSizeMode === null || dialogSizeMode === void 0 ? void 0 : dialogSizeMode.position) === null || _e === void 0 ? void 0 : _e.includes('LEFT')) || dialogSizeMode.widthMode === 'STRETCH') &&
                        jsx("span", { key: '3', className: 'resize-handler dialog-resize-left' },
                            jsx("div", { className: 'resize-handler-icon w-100' })),
                    (!((_f = dialogSizeMode === null || dialogSizeMode === void 0 ? void 0 : dialogSizeMode.position) === null || _f === void 0 ? void 0 : _f.includes('RIGHT')) || dialogSizeMode.widthMode === 'STRETCH') &&
                        jsx("span", { key: '4', className: 'resize-handler dialog-resize-right' },
                            jsx("div", { className: 'resize-handler-icon w-100' })))))));
}
const useFormatMessage = () => {
    const intl = useIntl();
    return React.useCallback((id, values) => {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessages[id] }, values);
    }, [intl]);
};
/**
 * Check if dialog is in local storage or dialogInfos(isClosed=true)
 * Always show fixed dlg triggered by link even when it's a confirmation dlg.
 */
function checkIsInLocalStore(storeProps) {
    var _a, _b;
    const { currentPageId, dialogProps, runtimeMode, timestamp, dialogInfos, dispatch, urlDlgId } = storeProps;
    const { dialogJson, isOpenByPage, runtime } = dialogProps;
    let isInStore = false;
    // splash or page dialog can't be url dlg by design.
    if (urlDlgId === dialogJson.id && !dialogJson.isSplash && !isOpenByPage && !dialogJson.confirmBeforeClose) {
        return isInStore;
    }
    if (dialogJson.isSplash || isOpenByPage) {
        const targetDlgId = dialogJson.isSplash ? dialogJson.id : `${currentPageId}-${dialogJson.id}`;
        if (runtime && dialogJson.interactionType &&
            ((dialogJson.confirmBeforeClose && !dialogJson.alwaysShowConfirmation) || !dialogJson.confirmBeforeClose)) {
            const keys = getDialogUniqueKey(targetDlgId, runtimeMode, timestamp);
            isInStore = readLocalStorage(keys.newKey) === 'true' || readLocalStorage(keys.oldKey) === 'true';
            if (isInStore) {
                setDialogInDialogInfos(dialogInfos, targetDlgId, dispatch); // save dlgInfo from localStore
            }
            if (!dialogJson.confirmBeforeClose && !isInStore) {
                isInStore = (_a = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[targetDlgId]) === null || _a === void 0 ? void 0 : _a.isClosed;
            }
        }
        else {
            isInStore = (_b = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[targetDlgId]) === null || _b === void 0 ? void 0 : _b.isClosed;
        }
    }
    return isInStore;
}
function setDialogInLocalStore(storeProps) {
    var _a;
    const { currentPageId, dialogProps, runtimeMode, timestamp, dialogInfos, dispatch } = storeProps;
    const { dialogJson, runtime } = dialogProps;
    const targetDlgId = dialogJson.isSplash ? dialogJson.id : `${currentPageId}-${dialogJson.id}`;
    if (runtime && dialogJson.interactionType &&
        ((dialogJson.confirmBeforeClose && !dialogJson.alwaysShowConfirmation) || (!dialogJson.confirmBeforeClose && ((_a = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[targetDlgId]) === null || _a === void 0 ? void 0 : _a.checked)))) {
        setLocalStorage(getDialogUniqueKey(targetDlgId, runtimeMode, timestamp).newKey, 'true');
    }
    setDialogInDialogInfos(dialogInfos, targetDlgId, dispatch);
}
function setDialogInDialogInfos(dialogInfos, targetDlgId, dispatch) {
    var _a;
    if (!((_a = dialogInfos === null || dialogInfos === void 0 ? void 0 : dialogInfos[targetDlgId]) === null || _a === void 0 ? void 0 : _a.isClosed)) {
        dialogInfos = (dialogInfos || SeamlessImmutable({})).setIn([targetDlgId, 'isClosed'], true);
        dispatch(dialogInfosChanged(dialogInfos));
    }
}
/**
 * Get dlg unique key
 * old: exb-appId-draft-dlgId-timestamp
   new: exb-appId-draft-timestamp-dlgId
 * @param dialogId
 * @param runtimeMode
 * @param timestamp
 * @returns string
 */
const getDialogUniqueKey = (dialogId, runtimeMode, timestamp) => {
    const localAppKey = getLocalStorageAppKey();
    const oldKey = `${localAppKey}-${runtimeMode === 'draft' ? 'draft' : 'release'}-${dialogId}-${timestamp}`;
    const newKey = `${localAppKey}-${runtimeMode}-${timestamp}-${dialogId}`;
    return { oldKey, newKey };
};
function loadJimuUIForFixed() {
    return __awaiter(this, void 0, void 0, function* () {
        return yield import('jimu-ui');
    });
}
export default FixedDialog;
